import React, {useState} from 'react';
import {COLORS} from '@fupa/fupa-uikit';
import {EmailAndPasswordLogin, redirectBackToCaller} from 'app/services/firebase';
import styled from 'styled-components';
import {useForm} from 'react-hook-form';
import {isAtInAddress, isFilled, isValidEmailAddress} from 'app/components/helpers/ValidationRules';
import {AuthHint} from 'app/components/authProvider/AuthHint';
import {StyledButton} from 'app/styles/content';
import {useSelector} from 'react-redux';
import {selectButtonType, selectJWTCookieName} from 'app/hooks/reduxCreateSelectorHooks';
import {TextField, InputAdornment, IconButton} from '@mui/material';
import {Visibility, VisibilityOff} from '@mui/icons-material';
import {sendEventToGA4} from 'app/components/helpers/gtagHelpers';

const Container = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const RightAlignedAuthHint = styled(AuthHint)`
  &&& {
    display: inline-flex;
    margin-top: 0;
    width: 100%;
    justify-content: flex-end;
  }
`;

const EmailFormLogin = ({setShowLoading, onError, redirectUrl}) => {
  const buttonType = useSelector(selectButtonType);
  const [showPassword, setShowPassword] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const {register, handleSubmit, errors} = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const jwtCookieName = useSelector(selectJWTCookieName);

  const onSubmit = ({email, password}) => {
    // function is only called, when inputs are valid
    // Reset error in case form is submitted a second time
    onError(null, '');
    setDisableButton(true);
    setShowLoading(true);
    EmailAndPasswordLogin(email, password)
      .then((userCredential) => {
        sendEventToGA4('login', {method: 'password'});
        redirectBackToCaller(userCredential.user, redirectUrl, jwtCookieName, 'password', error =>
          onError(error, email)
        ); // email-password does not have auth credential
      })
      .catch((error) => {
        setDisableButton(false);
        setShowLoading(false);
        onError(error, email);
      });
  };

  return (
    <Container onSubmit={handleSubmit(onSubmit)}>
      <TextField
        variant="standard"
        autoComplete='email'
        autoFocus={true}
        color='secondary'
        error={!!errors.email}
        helperText={errors.email ? errors.email.message : ' '}
        id='email-login'
        InputLabelProps={{required: false}}
        inputProps={{tabIndex: '1'}}
        inputRef={register({
          validate: {
            isFilled,
            isAtInAddress,
            isValidEmailAddress,
          },
        })}
        label='E-Mail'
        name='email'
        placeholder='max.mustermann@mail.de'
        required={true}
        type='email' />
      <TextField
        variant="standard"
        autoComplete='current-password'
        color='secondary'
        error={!!errors.password}
        helperText={errors.password ? errors.password.message : ' '}
        id='password-login'
        inputProps={{
          tabIndex: '1',
        }}
        inputRef={register({
          validate: isFilled,
        })}
        label='Passwort'
        name='password'
        placeholder='********'
        type={showPassword ? 'text' : 'password'}
        InputProps={{
          endAdornment: (
            <InputAdornment position='end'>
              <IconButton
                tabIndex='1'
                aria-label='Ändern der Sichtbarkeit des Passworts'
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                size="large">
                {showPassword ? (
                  <VisibilityOff style={{color: COLORS.darkGrey}} />
                ) : (
                  <Visibility style={{color: COLORS.darkGrey}} />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }} />
      <RightAlignedAuthHint linkedText='Passwort vergessen?' linkTo={'/auth/reset-password'} />
      <StyledButton tabIndex='1' justify='center' type={buttonType} label='Anmelden' disabled={disableButton} />
    </Container>
  );
};

export {EmailFormLogin};
